import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Select, Spinner, EmptyState } from "@/components/common";
import { ClipboardCheck, Check, X, AlertTriangle, Bandage, Save } from "lucide-react";

const STATI = {
  presente: { label: "Presente", icon: Check, cls: "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]" },
  assente: { label: "Assente", icon: X, cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]" },
  giustificato: { label: "Giustificato", icon: AlertTriangle, cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  infortunato: { label: "Infortunato", icon: Bandage, cls: "bg-[#F3E8FF] text-[#6B21A8] border-[#D8B4FE]" },
};
const ORDER = ["presente", "assente", "giustificato", "infortunato"];

export default function Presenze() {
  const { can } = useAuth();
  const editable = can("presenze", "edit");
  const [corsi, setCorsi] = useState([]);
  const [persone, setPersone] = useState({});
  const [corsoId, setCorsoId] = useState("");
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/corsi").then((r) => { setCorsi(r.data); if (r.data[0]) setCorsoId(r.data[0].id); });
    api.get("/persone").then((r) => setPersone(Object.fromEntries(r.data.map((p) => [p.id, p]))));
  }, []);

  useEffect(() => {
    if (!corsoId || !data) return;
    setLoading(true);
    api.get(`/presenze?corso_id=${corsoId}`).then((r) => {
      const sess = r.data.find((s) => s.data === data);
      setRecords(sess?.records || {});
    }).finally(() => setLoading(false));
  }, [corsoId, data]);

  const corso = corsi.find((c) => c.id === corsoId);
  const partecipanti = (corso?.partecipanti || []).map((id) => persone[id]).filter(Boolean);

  const cycle = (pid) => {
    if (!editable) return;
    const cur = records[pid];
    const next = cur ? ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length] : "presente";
    setRecords((r) => ({ ...r, [pid]: next }));
  };
  const setAll = (stato) => setRecords(Object.fromEntries(partecipanti.map((p) => [p.id, stato])));
  const save = async () => {
    try { await api.post("/presenze", { corso_id: corsoId, data, records }); toast.success("Presenze salvate"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Registro Presenze" subtitle="Tocca un badge per cambiare stato" />
      <Card className="p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Corso / Squadra" value={corsoId} onChange={(e) => setCorsoId(e.target.value)} className="w-auto min-w-[180px]" data-testid="presenze-corso-select">
            {corsi.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </Select>
          <div><label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Data</label>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="presenze-data" className="px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none" /></div>
          {editable && <Button variant="outline" size="sm" onClick={() => setAll("presente")} data-testid="attendance-mark-present-btn"><Check size={14} /> Tutti presenti</Button>}
          {editable && <Button onClick={save} data-testid="save-presenze-btn"><Save size={15} /> Salva</Button>}
        </div>
      </Card>

      {loading ? <Spinner /> : partecipanti.length === 0 ? <Card className="p-6"><EmptyState icon={ClipboardCheck} title="Nessun partecipante" hint="Aggiungi atleti al corso" /></Card> : (
        <Card className="overflow-hidden divide-y divide-slate-100">
          {partecipanti.map((p) => {
            const st = records[p.id]; const info = STATI[st];
            return (
              <div key={p.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50/50" data-testid={`presenza-row-${p.id}`}>
                <span className="font-medium text-slate-800">{p.cognome} {p.nome}</span>
                <button onClick={() => cycle(p.id)} data-testid={`presenza-toggle-${p.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${info ? info.cls : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                  {info ? <><info.icon size={15} /> {info.label}</> : "Segna"}
                </button>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
